import React from "react";
import HeroBackground from "./HeroBackground";
import LeadForm from "./LeadForm";
import FinalCTA from "./FinalCTA";
import SectionHeading from "./SectionHeading";
import { ArrowUpRight, CheckCircle2, MapPin, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n";

export default function CountryLayout({ countryKey, countryCode, sourcePage }) {
  const t = useT();
  const c = t.country[countryKey];
  const common = t.country.common;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <HeroBackground variant="full" />
        <div className="relative nx-container pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="nx-mono text-xs uppercase tracking-[0.3em] text-[#38D8FF] mb-5 inline-flex items-center gap-2 nx-fade-in">
            <MapPin className="w-3.5 h-3.5" /> {c.eyebrow}
          </div>
          <h1 className="nx-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl nx-fade-in">
            <span className="nx-gradient-text">{c.title}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/75 leading-relaxed nx-fade-in">{c.lede}</p>
          <p className="mt-4 max-w-2xl text-base text-white/60 leading-relaxed nx-fade-in">{c.body}</p>
          <div className="mt-8 flex flex-wrap gap-3 nx-fade-in">
            <a
              href="#country-lead-form"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#0057FF] hover:bg-[#2F80FF] text-white text-sm font-medium nx-cta-glow"
            >
              {common.formCta} <ArrowUpRight className="w-4 h-4" />
            </a>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-white/20 hover:border-[#38D8FF] text-white/85 text-sm font-medium"
            >
              {common.exploreLabel}
            </Link>
          </div>
        </div>
      </section>

      {/* Specialists + Industries */}
      <section className="nx-container py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <SectionHeading
            eyebrow={common.specialistsEyebrow}
            title={common.specialistsTitle}
            lede={common.specialistsLede}
          />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {c.specialists.map((sp, i) => (
              <div
                key={sp}
                className="nx-card rounded-xl p-4 flex items-center gap-4 hover:-translate-y-0.5 transition-transform"
              >
                <span className="nx-mono text-[#38D8FF] text-sm w-7">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <Sparkles className="w-4 h-4 text-white/60" />
                <span className="text-sm font-medium text-white/90">{sp}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <SectionHeading eyebrow={common.industriesEyebrow} title={common.industriesTitle} />
          <div className="mt-10 flex flex-wrap gap-2">
            {c.industries.map((ind) => (
              <span
                key={ind}
                className="px-4 py-2 rounded-full border border-white/15 text-sm text-white/85 bg-white/[0.02]"
              >
                {ind}
              </span>
            ))}
          </div>

          <div className="mt-12">
            <SectionHeading eyebrow={common.whyEyebrow} title={common.whyTitle} />
            <ul className="mt-8 space-y-3">
              {c.whyUs.map((w) => (
                <li key={w} className="flex items-start gap-3 text-sm text-white/85">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#38D8FF] shrink-0" />
                  {w}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Lead form */}
      <section id="country-lead-form" className="relative border-t border-white/10 bg-[#050F22]/60">
        <div className="nx-container py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow={common.formEyebrow}
              title={common.formTitle}
              lede={common.formLede}
            />
            <div className="mt-8 space-y-4 text-sm text-white/70">
              {common.bullets.map((b) => (
                <div key={b} className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#38D8FF]" />
                  {b}
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <LeadForm defaultCountry={c.eyebrow} sourcePage={sourcePage} />
          </div>
        </div>
      </section>

      <FinalCTA heading={common.finalHeading} ctaLabel={common.finalCta} />
    </div>
  );
}
